/**
 * Мини-игры в клиенте (план 2b, Task 8): открытая мини-игра, подбор по
 * триггерам и чистые помощники для стора.
 *
 * Открытая мини-игра `PendingMinigame` живёт в сторе и в сохранении:
 * `id`, состояние из `def.setup`, момент открытия и триггер, по которому
 * её предложили. Триггер нужен при завершении: исход decision-триггера
 * применяется к жизни сразу (`applyMinigame` движка), а его
 * `decisionOption` уходит в `answer()`; исход остальных триггеров ждёт
 * следующего тика как `YearInput.minigame`.
 *
 * Открытая дольше `STALE_MS` (вкладку закрыли и вернулись через час)
 * при загрузке разрешается ожидаемым исходом `expected()` — так же, как
 * «Пропустить» и как играет автопилот.
 *
 * Случайность: `Rng` жизни, fork `minigame/<возраст>/<id>` для `setup` и
 * `.fork('play')` для `play`; `Math.random` нет — та же жизнь с тем же
 * seed и теми же вводами воспроизводится на любом клиенте.
 */
import type { Dataset } from '@life/data';
import { createRng, type Life, type LogEntry, type MinigameOutcome, type Rng } from '@life/engine';
import { minigamesFor, PLACE_IDS, type MinigameDef, type PlaceId, type Trigger } from '@life/minigames';

export interface PendingMinigame {
  id: string;
  /** Состояние из `def.setup`; клиент его не меняет. */
  state: unknown;
  /** `Date.now()` открытия. */
  openedAt: number;
  trigger: Trigger;
  /**
   * Мини-игры, уже сыгранные для этого же решения (на `move.abroad` их две:
   * чемодан, затем граница). Хранится в сохранении, чтобы перезагрузка
   * между ними не предлагала первую игру снова; у остальных триггеров `[]`.
   */
  played: string[];
  /**
   * Мини-игра, открытая делом места (план 2d): исход сложится с базовым
   * (`mergeOutcomes`) и применится сразу через `applyActions` как действие
   * `place/action`; у мини-игр по триггерам поля нет.
   */
  place?: PlaceAction;
}

/** Дело места, которое ждёт исхода открытой мини-игры. */
export interface PlaceAction {
  place: PlaceId;
  action: string;
  /** Базовый исход визита (например, смена даёт деньги и усталость независимо от игры). */
  base?: MinigameOutcome;
}

/** Сыгранный исход, который применится на следующем тике как `YearInput.minigame`. */
export interface QueuedMinigame {
  id: string;
  outcome: MinigameOutcome;
}

/** Открытая дольше часа мини-игра разрешается ожидаемым исходом при загрузке (Review Focus 3). */
export const STALE_MS = 60 * 60 * 1000;

/**
 * Мини-игры, которые в режиме «пропускать тяжёлое» не открываются —
 * остаётся текстовая карточка и ожидаемый исход (спека: G3 «Буллинг»).
 */
export const HEAVY_MINIGAMES: readonly string[] = ['bullying'];

/** Мини-игры, рядом с которыми в `Host` всегда показан блок помощи (G3, G13). */
export const HELP_MINIGAMES: readonly string[] = ['bullying', 'refusal'];

export function isHeavyMinigame(id: string): boolean {
  return HEAVY_MINIGAMES.includes(id);
}

export function isHelpMinigame(id: string): boolean {
  return HELP_MINIGAMES.includes(id);
}

const TRIGGER_KINDS: readonly Trigger['kind'][] = ['decision', 'event', 'age', 'envelope', 'place'];

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

/** Проверка сохранённой мини-игры (мусор → не восстанавливается). */
export function isPendingMinigame(x: unknown): x is PendingMinigame {
  if (!isRecord(x)) return false;
  if (typeof x.id !== 'string' || typeof x.openedAt !== 'number' || !Number.isFinite(x.openedAt)) return false;
  if (!isRecord(x.trigger) || typeof x.trigger.kind !== 'string') return false;
  if (!Array.isArray(x.played) || !x.played.every((id) => typeof id === 'string')) return false;
  if (x.place !== undefined && !isPlaceAction(x.place)) return false;
  return (TRIGGER_KINDS as readonly string[]).includes(x.trigger.kind) && x.state !== undefined;
}

function isPlaceAction(x: unknown): x is PlaceAction {
  if (!isRecord(x) || typeof x.place !== 'string' || typeof x.action !== 'string') return false;
  if (!(PLACE_IDS as readonly string[]).includes(x.place)) return false;
  return x.base === undefined || isRecord(x.base);
}

export function isQueuedMinigame(x: unknown): x is QueuedMinigame {
  return isRecord(x) && typeof x.id === 'string' && isRecord(x.outcome);
}

export function isStale(pending: PendingMinigame, now = Date.now()): boolean {
  return now - pending.openedAt > STALE_MS;
}

/** Rng мини-игры: fork от seed жизни по возрасту и id; `play` берёт `.fork('play')`. */
export function minigameRng(life: Life, id: string): Rng {
  return createRng(life.seed).fork(`minigame/${life.age}/${id}`);
}

export interface TriggerOptions {
  gamblingEnabled: boolean;
  skipHeavy: boolean;
}

function allowed(def: MinigameDef, opts: TriggerOptions): boolean {
  return !(opts.skipHeavy && isHeavyMinigame(def.id));
}

/** Доступные мини-игры для триггера с учётом настроек (раздел E и тяжёлые темы). */
export function availableFor(trigger: Trigger, life: Life, ds: Dataset, opts: TriggerOptions): MinigameDef[] {
  return minigamesFor(trigger, life, ds, { gamblingEnabled: opts.gamblingEnabled }).filter((def) => allowed(def, opts));
}

/** Мини-игры висящего решения в порядке реестра (на `move.abroad` их две — играются по очереди). */
export function decisionMinigames(life: Life, ds: Dataset, opts: TriggerOptions): MinigameDef[] {
  const decision = life.pendingDecision;
  if (decision === null) return [];
  return availableFor({ kind: 'decision', decision: decision.kind }, life, ds, opts);
}

/**
 * Мини-игра после тика: сначала по событиям года (`LogEntry.key`), затем
 * по возрасту; раздел E (ставки по возрасту) — последним, чтобы не
 * заслонять этапы жизни. В год — не больше одной (одно
 * `YearInput.minigame`), один id — один раз (бюджет G12 совпадает и по
 * событию, и по возрасту).
 */
export function tickMinigame(
  life: Life,
  entries: readonly LogEntry[],
  ds: Dataset,
  opts: TriggerOptions,
): { def: MinigameDef; trigger: Trigger } | null {
  const found: { def: MinigameDef; trigger: Trigger }[] = [];
  const seen = new Set<string>();
  const add = (trigger: Trigger) => {
    for (const def of availableFor(trigger, life, ds, opts)) {
      if (seen.has(def.id)) continue;
      seen.add(def.id);
      found.push({ def, trigger });
    }
  };
  const events = new Set(entries.map((e) => e.key));
  for (const event of events) add({ kind: 'event', event });
  add({ kind: 'age', min: life.age, max: life.age });
  return found.find((f) => f.def.section !== 'E') ?? found[0] ?? null;
}

/** Исход «пропустить»/автопилота. */
export function expectedOf(def: MinigameDef, state: unknown, life: Life): MinigameOutcome {
  return def.expected(state, life);
}
